/**
 * Virtual modifier state for touch-first UI surfaces that need to hold
 * Shift / Alt / Cmd without relying on a physical keyboard.
 *
 * This module is intentionally panel-agnostic. It only owns modifier state
 * and capture-phase event injection so callers like the tool palette can
 * expose the controls however they want.
 */

import { create } from "zustand";

export type VirtualModifier = "shift" | "alt" | "cmd";

interface VirtualModifierState {
  activeModifiers: Set<VirtualModifier>;
  toggleModifier: (modifier: VirtualModifier) => void;
  clearModifiers: () => void;
}

export const useVirtualModifiers = create<VirtualModifierState>((set) => ({
  activeModifiers: new Set(),
  toggleModifier: (modifier) =>
    set((state) => {
      const next = new Set(state.activeModifiers);
      if (next.has(modifier)) next.delete(modifier);
      else next.add(modifier);
      return { activeModifiers: next };
    }),
  clearModifiers: () => set({ activeModifiers: new Set() }),
}));

function injectVirtualModifiers(event: Event): void {
  const { activeModifiers } = useVirtualModifiers.getState();
  if (activeModifiers.size === 0) return;

  if (!(event instanceof MouseEvent) && !(event instanceof KeyboardEvent)) {
    return;
  }

  if (activeModifiers.has("shift") && !event.shiftKey) {
    Object.defineProperty(event, "shiftKey", { value: true });
  }
  if (activeModifiers.has("alt") && !event.altKey) {
    Object.defineProperty(event, "altKey", { value: true });
  }
  if (activeModifiers.has("cmd")) {
    if (!event.metaKey)
      Object.defineProperty(event, "metaKey", { value: true });
    if (!event.ctrlKey)
      Object.defineProperty(event, "ctrlKey", { value: true });
  }
}

const VIRTUAL_MODIFIER_EVENTS = [
  "pointerdown",
  "pointermove",
  "pointerup",
  "pointerleave",
  "pointercancel",
  "mousedown",
  "mousemove",
  "mouseup",
  "wheel",
  "click",
  "dblclick",
  "contextmenu",
  "keydown",
  "keyup",
] as const;

for (const type of VIRTUAL_MODIFIER_EVENTS) {
  document.addEventListener(type, injectVirtualModifiers, { capture: true });
}
